import { ISectionLogger } from "@multiweb/logging";
import { IRowData } from "@multiweb/types/contracts";
import { CaseInsensitiveMap } from "@multiweb/types/multiweb";
import { ExtendedPropertiesType } from "UI/types";
import { IDataSource, IDataSourceLoadInfo, IOrderBy } from "../../Global/Base/DataSources/Interfaces";
import { DeferredStateful, PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { IAwaitableEvent } from "../UI.BaseControls/AwaitableEvent";
import { GenericControl } from "../UI.BaseControls/GenericControl";
import { IBooleanDetailValueEvent } from "../UI.BaseControls/IEvent";
import { createElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { Column } from "./DataGridSubcontrols/Columns/Column";
import { IDataRowElement } from "./DataGridSubcontrols/Components/IDataRow";
import { IHeaderRow } from "./DataGridSubcontrols/Components/IHeaderRow";
import { IDataGrid } from "./IDataGrid";
import { ICellValueChangedPrewAwaitableEvent, IColumnOrderingChangedEvent, IColumnPositionChangedEvent, IDataGridDataShownEvent, IDataGridSelectionChangedEvent, IDataGridSelectionChangedEventData } from "./IEvent";

export abstract class BaseTreeViewGrid<R extends IDataRowElement> extends HTMLElement implements IDataGrid<R> {
    //#region Fields
    private readonly _logger: ISectionLogger;
    private _isAttached: boolean;
    protected _requestClearRows = false;
    protected _columns: Column[] = [];
    protected _orderBy: IOrderBy[];
    protected _selectedDataRows: Array<R> = [];
    protected _dataTable: HTMLElement;
    protected _dataRowFactory?: (dataGrid: IDataGrid<R>, data: IRowData) => R;
    protected _areDataShowedDefferred: DeferredStateful<void>;
    private _extendedProperties = new CaseInsensitiveMap<ExtendedPropertiesType>();
    //#endregion Fields

    //#region Properties
    /**
     * Zda je grid v režimu pouze pro čtení
     */
    public interactive: boolean = false;
    /**
     * Poslední vybraný záznam
     */
    public lastClickedRow: R;
    /**
     * Řídí možnost výběru více záznamů najednou
     */
    public multiSelect: boolean = false;
    /**
     * Informace zda jsou vybrány všechny řádky (i ty ještě nezobrazené)
     */
    public isSelectedAll: boolean = false;
    /**
     * Zda se má automaticky vybrat první záznam
     */
    public autoSelectFirstRecord: boolean = false;
    /**
     * Vybere první řádek pouze pokud je načtený jen jeden záznam
     */
    public autoSelectOnlyIfOneRow: boolean = false;
    /**
     * Vrací informaci o tom, zda je element viditelný (a tím pádem zda má cenu renderovat obsah)
     */
    public readonly canValueLoad: boolean = false;
    /**
     * Informace o tom, zda už proběhla inicializace gridu
     */
    public initialized: boolean = false;
    /**
     * Řídí, zda se má vykreslovat hlavička gridu
     */
    public renderHeader: boolean = true;

    public fetchCount: number;
    /**
     * Zda se mají vykreslovat ovládací prvky pro záznam
     */
    public renderControlElement: boolean = false;
    /**
     * index řádku při pohybu šipkami up/down + shift
     */
    public keyPressRowIndex: number;
    /**
     * Kolekce řádků zobrazených v seznamu
     */
    public dataRows: Array<R> = [];
    /**
     * Řádek hlavičky seznamu
     */
    public headerRow: IHeaderRow<R>;
    /**
     * Název objektu, nad kterým pracujeme
     */
    public objectName: string;

    /**
     * Kolekce sloupců zobrazených v seznamu
     */
    public abstract columns: Column[];
    public abstract dataSource: IDataSource;
    public abstract orderBy: IOrderBy[];

    public setFooterContent(_content: HTMLElement): void {
        // Patičku zatím není třeba podporovat pro BaseTreeViewGrid. Potvrzeno od Jana Novotného.
        this._logger.warn("Footer is not yet supported. Please do not use footer related methods.");
    }

    public setRenderFooter(_renderFooter: boolean): void {
        // Patičku zatím není třeba podporovat pro BaseTreeViewGrid. Potvrzeno od Jana Novotného.
        this._logger.warn("Footer is not yet supported. Please do not use footer related methods.");
    }

    public get dataShowedPromise(): Promise<void> {
        return this._areDataShowedDefferred.promise;
    }

    public set visible(v: boolean) {
        this.classList.toggle("Hidden", !v);
    }

    public get visible(): boolean {
        return !this.classList.contains("Hidden");
    }

    public set enabled(v: boolean) {
        this.classList.toggle("disabled", !v);
    }

    public get enabled(): boolean {
        return !this.classList.contains("disabled");
    }

    public get isAttached(): boolean {
        return this._isAttached;
    }

    public get selectedRows(): Array<R> {
        return this._selectedDataRows;
    }

    private _skippedRowsCount: number = 0;
    public get skippedRowsCount(): number {
        return this._skippedRowsCount;
    }
    public set skippedRowsCount(value: number) {
        this._skippedRowsCount = value;
    }

    public get startIndex(): number {
        return this.dataRows.length + this._skippedRowsCount;
    }

    public get allDataRead(): boolean {
        return false;
    }
    //#endregion Properties

    constructor(logger: ISectionLogger, self?: any) {
        super();
        this._logger = logger;
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);

        this.classList.add("tree-view-grid", "dataGrid");

        this._dataTable = createElement({ type: "ul", classes: "list-div" });
        this._areDataShowedDefferred = PromiseUtils.deferStateful<void>();
    }

    /**
     * Vrátí vybrané dataKey řádků, pokud je výběr složitější vrací je pomocí podmínky jinak vrátí pole dataKeys
     * @param forceCondition pokud je true, vždy vrací podmínku, výchozí false
     */
    public getSelectionData(forceCondition: boolean = false): string | string[] {
        if (forceCondition) throw Error("Support only for id list");
        return this.selectedRows.map(item => item.dataKey);
    }

    // ReSharper disable once InconsistentNaming
    protected connectedCallback(): void {
        this._isAttached = true;
        GenericControl.callEvent(this, "attached");
    }

    // ReSharper disable once InconsistentNaming
    protected disconnectedCallback(): void {
        this._isAttached = false;
        GenericControl.callEvent(this, "detached");
        this.unregisterEventListener();
    }

    // ReSharper disable once InconsistentNaming
    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public registerEventListener(type: "SelectionChanged", listener: (ev: IDataGridSelectionChangedEvent) => void, customId?: string): void;
    public registerEventListener(type: "SelectionChangedPreview", listener: (ev: IAwaitableEvent<boolean>) => void, customId?: string): void;
    public registerEventListener(type: "SelectionAll", listener: (ev: CustomEvent) => void, customId?: string): void;
    public registerEventListener(type: "CellValueChangedPreview", listener: (ev: ICellValueChangedPrewAwaitableEvent) => void, customId?: string): void;
    public registerEventListener(type: "Attached", listener: (ev: CustomEvent) => void, customId?: string): void;
    public registerEventListener(type: "DataShown", listener: (ev: IDataGridDataShownEvent) => void, customId?: string): void;
    public registerEventListener(type: "ReadOnlyChange", listener: (ev: IBooleanDetailValueEvent) => void, customId?: string): void;
    public registerEventListener(type: "BeforeOrder", listener: (ev: CustomEvent) => void, customId?: string): void;
    public registerEventListener(type: "OrderingChanged", listener: (ev: IColumnOrderingChangedEvent) => void, customId?: string): void;
    public registerEventListener(type: "ColumnPositionChanged", listener: (ev: IColumnPositionChangedEvent) => void, customId?: string): void;
    public registerEventListener(type: string, listener: EventListener, customId?: string): void;
    public registerEventListener(type: any, listener: any, _customId?: string): void {
        GenericControl.registerEventListener(this as HTMLElement, type, listener);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        GenericControl.unregisterEventListener(this as HTMLElement, event, action);
    }

    public selectAllRows(): void {
        throw new Error("Method not implemented.");
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    /**
     * Zaregistruje tovární metodu, pomocí které se budou vytvářet elementy řádků
     * @param factory Tovární metoda vytvářející elementy řádku
     */
    public registerDataRowFactory(factory: (dataGrid: IDataGrid<R>, data: IRowData) => R): void {
        this._dataRowFactory = factory;
    }

    /**
     * Umožňuje zaregistrovat vlastní tovární funkce, která se používá při vytváření ovládacích prvků pro každý řádek
     * @param _factory Tovární funkce
     */
    public registerRowControlsFactory(_factory: (dataKeyFetch: () => string) => HTMLElement): void {
        // nevyužívá
    }

    //#endregion Zpracování metod z globální třídy GenericControl

    //#region Methods
    /**
     * Metoda pro odstranění všech řádků datagridu
     */
    public abstract clearDataRows(): void;

    /**
     * Spustí načtení dat do podřízené větve stromu
     * @param parent Prvek, pro který mají být načteny podřízené záznamy
     */
    protected abstract _loadChildData(parent: R): Promise<void>;

    /**
     * Vytvoří nové řádky s daty
     * @param eventData Informace o načtených datech
     */
    protected abstract _showData(eventData: IDataSourceLoadInfo): void;

    /**
     * Vytvoří nový řádek
     * @param data Data ke zobrazení
     */
    protected _createRow(data: IRowData): R {
        const dataRow = this._dataRowFactory(this, data);
        dataRow.init(data, this, null);
        this.dataRows.push(dataRow);

        dataRow.addEventListener("selectedRecordChanged", () => {
            while (this._selectedDataRows.length > 0) {
                // Všechny označené řádky zrušíme.
                this._selectedDataRows.pop().isSelected = false;
            }

            // Aktuální řádek vybereme.
            dataRow.isSelected = true;

            // Přidáme nově vybraný řádek do seznamu vybraných.
            this._selectedDataRows.push(dataRow);

            // Vyvoláme událost o změně vybraného řádku.
            this._triggerSelectionChanged(this._selectedDataRows.length > 1 ? "-1" : dataRow.dataKey, data);
        });

        return dataRow;
    }

    /**
     * Označí požadovaný řádek
     * @param _dataKey Identifikátor, na základě kterého má být označovaný záznam nalezen
     */
    public async selectRow(_dataKey: string): Promise<R> {
        throw new Error("Not implemented");
    }

    /**
     * Odstraní požadovaný řádek
     * @param _dataKey Identifikátor, na základě kterého má být označovaný záznam nalezen
     */
    public removeRow(_dataKey: any): Promise<void> {
        throw new Error("Not implemented");
    }

    /**
     * Vloží nový element reprezentující existující záznam a vrátí ho
     * @param _dataRow Data nového záznamu
     */
    public insertExistingRecord(_dataRow: IRowData): R {
        throw new Error("Not implemented");
    }

    /**
     * Vytvoří a vloží nový element reprezentující nový záznam
     * @param _dataRow Data nového záznamu
     */
    public insertNewRecord(_dataRow: IRowData): R {
        throw new Error("Not implemented");
    }

    /**
     * Označí předchozí záznam
     */
    public prevRecord(_trigger?: boolean): void {
        throw new Error("Not implemented");
    }

    /**
     * Označí následující záznam
     */
    public nextRecord(_trigger?: boolean): void {
        throw new Error("Not implemented");
    }

    /**
     * Znovu načte a vykreslí data
     */
    public refreshData(): Promise<void> {
        throw new Error("Not implemented");
    }
    /**
     * Znovu vykreslí datové řádky
     */
    public reRenderData(): Promise<void> {
        throw new Error("Not implemented");
    }
    /**
     * Vola se pokud je resizovan formular nebo cokoliv jineho na co potrebuje grid zareagovat a docist zaznamy kvuli ztrate scrollbaru
     */
    public resizeGrid(_eventData?: IDataSourceLoadInfo): void {
        throw new Error("Not implemented");
    }

    public resumeLoading(): void {
        throw new Error("Method not implemented.");
    }

    /**
     * Odstraní všechny vybrané řádky
     */
    public removeSelectedRows(): void {
        throw new Error("Not implemented");
    }

    /**
     * Zruší označení předaného záznamu
     * @param _dataKey Klíč záznamu, kterému se má zrušit označení
     */
    public deselectRow(_dataKey: string): void {
        throw new Error("Not implemented");
    }

    /**
     * Nastaví používané filtry
     */
    public setFilters(_filters: { attrName: string; filterElement: HTMLSpanElement }[]): void {
        throw new Error("Not implemented");
    }

    /**
     * Smazani oznacenych radku
     */
    public clearSelectedRows(): void {
        while (this.selectedRows.length != 0) {
            (this.selectedRows.pop()).isSelected = false;
        }
    }

    public clearIsSelectedAllIfNeeded(): void {
        // Schválně prázdné. Stromy isSelectedAll neřeší.
    }

    /**
     * Vyvolává událost informující o změně výbraného záznamu.
     * @param id ID záznamu
     * @param data Data záznamu
     */
    protected _triggerSelectionChanged(id: string, data?: IRowData): void {
        this.callEvent("SelectionChanged", { dataGrid: this, rowID: id, data: data } as IDataGridSelectionChangedEventData, true, true);
    }

    public getExtendedProperties(attrName: string): ExtendedPropertiesType {
        return this._extendedProperties.get(attrName);
    }

    public setExtendedProperties(extendedProperties: CaseInsensitiveMap<ExtendedPropertiesType>): void {
        this._extendedProperties = extendedProperties;
    }

    public dispose(): void {
        // Prázdná metoda
    }

    //#endregion Methods
}
